/* Curated collection quick filters; retain Shopify's server-rendered cards and native sort/facets. */
if (!customElements.get('sale-collection-filters')) {
  customElements.define('sale-collection-filters', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.onClick = this.onClick.bind(this);
      this.onNativeChange = this.syncNativeForms.bind(this);
      this.addEventListener('click', this.onClick);
      this.section = this.closest('[data-section-type="collection"]');
      this.section.addEventListener('change', this.onNativeChange, true);
      // Desktop copy of the controls in the collection toolbar, which Prestige keeps when it reloads the product grid.
      this.toolbar = this.section.querySelector('[data-sale-toolbar]');
      this.toolbar?.addEventListener('click', this.onClick);
      history.replaceState({ ...history.state, path: window.location.href }, '', window.location.href);
      this.initialize();
    }

    disconnectedCallback() {
      this.controller.abort();
      this.removeEventListener('click', this.onClick);
      this.toolbar?.removeEventListener('click', this.onClick);
      this.section.removeEventListener('change', this.onNativeChange, true);
    }

    findAll(selector) {
      return [...this.querySelectorAll(selector), ...(this.toolbar?.querySelectorAll(selector) || [])];
    }

    async initialize() {
      const wrapper = this.querySelector('[data-sale-pages]');
      const pages = Number(wrapper?.dataset.salePages || 1);
      const currentPage = Number(wrapper?.dataset.salePage || 1);
      const currentCards = Array.from(this.querySelectorAll('[data-sale-product]'));
      const cardsByPage = new Map([[currentPage, currentCards]]);

      try {
        // Don't offer filters over a partial page. Keep normal pagination available if loading fails.
        if (pages > 1) this.querySelector('[data-sale-loading]').hidden = false;
        for (let page = 1; page <= pages; page += 1) {
          if (page === currentPage) continue;
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('page', page);
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Could not load the collection');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = document.querySelector('sale-collection-filters');
          if (Number(next?.querySelector('[data-sale-page]')?.dataset.salePage) !== page) {
            throw new Error('Incomplete collection response');
          }
          cardsByPage.set(page, Array.from(next.querySelectorAll('[data-sale-product]')));
        }
        if (!this.isConnected) return;

        this.cards = Array.from(cardsByPage.keys()).sort((a, b) => a - b)
          .flatMap(page => cardsByPage.get(page)).filter(card => card.querySelector('.ProductItem'));
        if (pages > 1) {
          this.querySelector('.ProductList').replaceChildren(...this.cards);
          if (typeof window.collection_swatches === 'function') window.collection_swatches();
          this.querySelectorAll('.Pagination').forEach(element => { element.hidden = true; });
          // Prestige removes the grid's bottom margin when pagination follows it; with the pagination gone, restore the
          // spacing a single-page collection has so the last row doesn't touch the next section.
          this.querySelector('.ProductList').classList.remove('ProductList--removeMargin');
          const url = new URL(window.location.href);
          url.searchParams.delete('page');
          history.replaceState({ ...history.state, path: url.href }, '', url);
        }
        this.buttons = this.findAll('[data-sale-group]');
        this.buttons.forEach(button => { button.dataset.saleOrder = [...button.parentElement.children].indexOf(button); });
        this.readSelection();
        this.setAttribute('data-ready', '');
        if (this.toolbar) this.toolbar.hidden = false;
        this.findAll('[data-sale-controls]').forEach(controls => { controls.hidden = false; });
        this.render();
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.querySelector('[data-sale-error]').hidden = false;
          this.findAll('[data-sale-controls]').forEach(controls => { controls.hidden = true; });
        }
      } finally {
        if (this.isConnected) this.querySelector('[data-sale-loading]').hidden = true;
      }
    }

    readSelection() {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      // Armchairs now belong to Modo Sofa; keep previously shared filter links useful.
      if (params.get('sale_product') === 'armchair') {
        params.set('sale_product', 'modo');
        history.replaceState({ ...history.state, path: url.href }, '', url);
      }
      this.selection = {};
      for (const group of ['space', 'product']) {
        const value = params.get(`sale_${group}`) || 'all';
        this.selection[group] = this.buttons.some(button => button.dataset.saleGroup === group && button.dataset.saleValue === value) ? value : 'all';
      }
    }

    matches(card, selection) {
      return (selection.space === 'all' || card.dataset.saleSpace === selection.space)
        && (selection.product === 'all' || card.dataset.saleFamily === selection.product);
    }

    render() {
      let count = 0;
      for (const card of this.cards) {
        card.hidden = !this.matches(card, this.selection);
        if (!card.hidden) count += 1;
      }
      for (const button of this.buttons) {
        const { saleGroup: group, saleValue: value } = button.dataset;
        const selected = this.selection[group] === value;
        const optionCount = this.cards.filter(card => this.matches(card, { ...this.selection, [group]: value })).length;
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = optionCount === 0 && !selected && value !== 'all';
        button.querySelector('[data-sale-count]').textContent = optionCount;
        // Hide choices absent from the full native result set; retain a selected deep link so it can be cleared.
        button.hidden = value !== 'all' && !selected
          && !this.cards.some(card => (group === 'space' ? card.dataset.saleSpace : card.dataset.saleFamily) === value);
      }
      // Product choices that still have matches come first, so the pills a phone shows before scrolling are always
      // usable; the impossible ones stay at the end, disabled. Rows are only touched when their order changes.
      for (const row of new Set(this.buttons.filter(button => button.dataset.saleGroup === 'product').map(button => button.parentElement))) {
        const ordered = [...row.children].sort((a, b) => (a.disabled - b.disabled) || (a.dataset.saleOrder - b.dataset.saleOrder));
        if (ordered.some((button, index) => button !== row.children[index])) {
          row.append(...ordered);
          row.parentElement.scrollLeft = 0;
        }
      }
      for (const status of this.findAll('[data-sale-result-count]')) {
        status.textContent = `${count} ${count === 1 ? status.dataset.singular : status.dataset.plural}`;
      }
      for (const reset of this.findAll('[data-sale-controls] [data-sale-reset]')) {
        reset.hidden = this.selection.space === 'all' && this.selection.product === 'all';
      }
      this.querySelector('[data-sale-empty]').hidden = count > 0 || this.cards.length === 0;
      this.syncNativeForms();
    }

    onClick(event) {
      const button = event.target.closest('[data-sale-group], [data-sale-reset]');
      if (!button || !this.selection) return;
      if (button.hasAttribute('data-sale-reset')) {
        this.selection = { space: 'all', product: 'all' };
      } else {
        this.selection[button.dataset.saleGroup] = button.dataset.saleValue;
      }
      const url = new URL(window.location.href);
      for (const [group, value] of Object.entries(this.selection)) {
        if (value === 'all') url.searchParams.delete(`sale_${group}`);
        else url.searchParams.set(`sale_${group}`, value);
      }
      url.searchParams.delete('page');
      // Let Prestige restore the complete server state when navigating back or forward.
      history.pushState({ path: url.href }, '', url);
      this.render();
      if (button.hidden) {
        // Only one copy of the controls is displayed at a time; move focus to the visible one.
        this.findAll('[data-sale-group][aria-pressed="true"]').find(element => element.offsetParent)?.focus();
      }
    }

    syncNativeForms() {
      if (!this.selection) return;
      const params = new URL(window.location.href).searchParams;
      for (const form of this.section.querySelectorAll('.collection-filters-form')) {
        for (const name of ['sale_space', 'sale_product', 'preview_theme_id', 'sale_filters_preview']) {
          let input = form.querySelector(`input[name="${name}"]`);
          const value = params.get(name);
          if (!value) { input?.remove(); continue; }
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.append(input);
          }
          input.value = value;
        }
      }
    }
  });
}
